import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiArticlesReferencesQuery,
  useGetApiCategoriesArticleReferencesByIdQuery,
  useGetApiCategoryTreeQuery,
  usePutApiCategoryTreeSetDisabledStateMutation,
} from '../../../redux/store/api/api';
import {
  selectActivateElementsDialogOpened,
  selectActiveVersion,
  selectContextArticle,
  selectContextCategory,
  setActivateElementsDialogOpened,
  setContextArticle,
  setContextCategory,
  setFocusedTreeItem,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';
import {
  useGetArticleIdsForElementIds,
  useGetCategoryIdsForElementIds,
} from '../../../hooks/useGetArticlesOrCategoriesForElementIds';
import {
  articleIdUrlParam,
  categoryIdUrlParam,
} from '../../../shared/constants';

interface IActivateElementsDialogProps {
  selectedIds: string[];
}

function ActivateElementsDialog({ selectedIds }: IActivateElementsDialogProps) {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const dialogShow = useAppSelector(selectActivateElementsDialogOpened);
  const articles = useGetArticleIdsForElementIds(dialogShow ? selectedIds : []);
  const categories = useGetCategoryIdsForElementIds(
    dialogShow ? selectedIds : [],
  );
  const contextCategory = useAppSelector(selectContextCategory);
  const contextArticle = useAppSelector(selectContextArticle);
  const activeVersion = useAppSelector(selectActiveVersion);
  const [setDisableState, { isError, isLoading, error }] =
    usePutApiCategoryTreeSetDisabledStateMutation();
  const [searchParams] = useSearchParams();
  const activeArticleId: string = searchParams.get(articleIdUrlParam) || '';
  const activeCategoryId: string = searchParams.get(categoryIdUrlParam) || '';
  const { refetch: refetchGetCategoriesTree } = useGetApiCategoryTreeQuery();
  const { refetch: refetchArticleReferences } =
    useGetApiArticlesReferencesQuery(
      activeArticleId && activeVersion.isRecentVersion && activeVersion.id
        ? {
            articleId: activeArticleId,
            versionId: activeVersion.id,
          }
        : skipToken,
    );
  const { refetch: refetchCategoryReferences } =
    useGetApiCategoriesArticleReferencesByIdQuery(
      activeCategoryId
        ? {
            id: activeCategoryId,
          }
        : skipToken,
    );

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'ActivateElementsError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  const handleActivateElements = () => {
    const articleIds = contextArticle.id ? [contextArticle.id] : articles;
    const categoryIds = contextCategory.id ? [contextCategory.id] : categories;
    setDisableState({
      categoryTreeBatchOperationItems: {
        articleIds,
        categoryIds,
      },
      disabled: false,
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'ActivateElementsSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
        dispatch(setActivateElementsDialogOpened(false));
        refetchGetCategoriesTree();
        dispatch(
          setFocusedTreeItem(
            contextCategory?.id || contextArticle?.id || selectedIds[0],
          ),
        );

        // refetch references if any article is open
        if (activeArticleId) {
          refetchArticleReferences();
        }

        // refetch categories if category view is open
        if (activeCategoryId) {
          refetchCategoryReferences();
        }
      });
  };

  return (
    <CustomDialog
      titleId='ActivateElementsDialog'
      dialogTitle={translation('activateElements')}
      show={dialogShow}
      closeFunction={() => {
        dispatch(setActivateElementsDialogOpened(false));
        dispatch(setContextCategory({}));
        dispatch(setContextArticle({}));
      }}
      actionFunction={handleActivateElements}
      closeTitle={translation('cancel')}
      actionTitle={translation('activate')}
      actionButtonDisabled={isLoading}>
      {isLoading && <Loader />}
      {!isLoading && (
        <p>
          {translation(
            contextArticle.id || contextCategory.id
              ? 'shouldActivateSelectedElement'
              : 'shouldActivateSelectedElements',
          )}
        </p>
      )}
    </CustomDialog>
  );
}

export default ActivateElementsDialog;
